import {
  AgentStep,
  Messages,
  ReasoningLevel,
  Role,
  StreamAction,
} from "@/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import i18n from "i18next";
import isEqual from "lodash/isEqual";

interface SidebarState {
  isOpen: boolean;
  toggle: () => void;
}

interface MessagesState {
  messages: Messages;
  setMessages: (messages: Messages) => void;
  references: Record<string, any>;
  setReferences: (references: Record<string, any>) => void;
  pushReference: (reference: object) => void;
  agentSteps: Record<string, { currentStep: number; steps: Array<AgentStep> }>;
  setAgentSteps: (
    conversationId: string,
    data: { currentStep: number; steps: Array<AgentStep> } | undefined,
  ) => void;
  streamActions: Record<string, Array<StreamAction>>;
  setStreamActions: (
    conversationId: string,
    actions: Array<StreamAction> | undefined,
  ) => void;
}

interface PlaygroundParametersState {
  llm_config_id: string;
  planner_llm_config: string;
  mode: string;
  temperature: number;
  max_tokens: number;
  language: string;
  reasoning_level: ReasoningLevel;
  blueprint_id?: string;
  setLlmConfigId: (id: string) => void;
  setPlannerLlmConfig: (id: string) => void;
  setMode: (mode: string) => void;
  setTemperature: (temperature: number) => void;
  setMaxTokens: (maxTokens: number) => void;
  setLanguage: (language: string) => void;
  setReasoningLevel: (reasoningLevel: ReasoningLevel) => void;
  setBlueprintId: (id: string | undefined) => void;
  reset: () => void;
}

const useSidebarStore = create<SidebarState>()((set) => ({
  isOpen: typeof window !== "undefined" ? window.innerWidth >= 768 : true,
  toggle: () => set((state) => ({ isOpen: !state.isOpen })),
}));

const useMessagesStore = create<MessagesState>((set) => ({
  messages: [],
  setMessages: (messages) =>
    set(() => ({
      messages,
    })),
  references: {},
  setReferences: (references) =>
    set(() => ({
      references,
    })),
  pushReference: (references) => {
    set((oldState) => {
      const oldReferences = oldState.references as Record<
        string,
        Record<string, any>
      >;
      const newReferences = references as Record<string, Record<string, any>>;

      const mergedReferences = { ...oldReferences };

      for (const category in newReferences) {
        mergedReferences[category] = {
          ...(mergedReferences[category] || {}),
          ...newReferences[category],
        };
      }

      if (isEqual(oldReferences, mergedReferences)) {
        return oldState;
      }

      return {
        references: mergedReferences,
      };
    });
  },
  agentSteps: {},
  setAgentSteps: (conversationId, data) =>
    set((state) => {
      if (data === undefined) {
        const newAgentSteps = { ...state.agentSteps };
        delete newAgentSteps[conversationId];
        return { agentSteps: newAgentSteps };
      }
      return {
        agentSteps: {
          ...state.agentSteps,
          [conversationId]: data,
        },
      };
    }),
  streamActions: {},
  setStreamActions: (conversationId, actions) =>
    set((state) => {
      if (actions === undefined) {
        const newStreamActions = { ...state.streamActions };
        delete newStreamActions[conversationId];
        return { streamActions: newStreamActions };
      }
      return {
        streamActions: {
          ...state.streamActions,
          [conversationId]: actions,
        },
      };
    }),
}));

const usePlaygroundParametersStore = create<PlaygroundParametersState>()(
  persist(
    (set) => ({
      llm_config_id: "",
      planner_llm_config: "",
      mode: "agentic",
      temperature: 0,
      max_tokens: 2048,
      language: i18n.language || "en",
      reasoning_level: "medium",
      blueprint_id: undefined,
      setLlmConfigId: (id) => set({ llm_config_id: id }),
      setPlannerLlmConfig: (id) => set({ planner_llm_config: id }),
      setMode: (mode) => set({ mode }),
      setTemperature: (temperature) => set({ temperature }),
      setMaxTokens: (maxTokens) => set({ max_tokens: maxTokens }),
      setLanguage: (language) => set({ language }),
      setReasoningLevel: (reasoning_level) => set({ reasoning_level }),
      setBlueprintId: (blueprint_id) => set({ blueprint_id }),
      reset: () =>
        set({
          llm_config_id: "",
          planner_llm_config: "",
          mode: "agentic",
          temperature: 0,
          max_tokens: 2048,
          language: i18n.language || "en",
          reasoning_level: "medium",
          blueprint_id: undefined,
        }),
    }),
    {
      name: "playground-parameters-storage",
      version: 2,
      migrate: (persistedState: any) => ({
        ...persistedState,
        mode: "agentic",
      }),
    },
  ),
);

export { useSidebarStore, useMessagesStore, usePlaygroundParametersStore };
